import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './WordleGame.css';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import DayQuestionPanel from './DayQuestionPanel';
import { useSelector } from 'react-redux';
import { demoGatePopupAndRedirect, isUserDemoBypass } from '../../utils/demoGate';

const ROWS = 6;
const COLS = 5;

const KEY_RANK = { absent: 0, present: 1, correct: 2 };

function mergeKeyHint(prev, letter, hint) {
  const cur = prev[letter] || 'absent';
  if (KEY_RANK[hint] >= KEY_RANK[cur]) {
    return { ...prev, [letter]: hint };
  }
  return prev;
}

/** Standard Wordle-style evaluation (greens first, then yellows with letter counts). */
function evaluateWordleGuess(guess, answer) {
  const g = guess.toUpperCase();
  const a = answer.toUpperCase();
  if (g.length !== COLS || a.length !== COLS) return null;
  const result = Array(COLS).fill('absent');
  const answerChars = a.split('');
  const guessChars = g.split('');
  const usedInAnswer = Array(COLS).fill(false);

  for (let i = 0; i < COLS; i++) {
    if (guessChars[i] === answerChars[i]) {
      result[i] = 'correct';
      usedInAnswer[i] = true;
    }
  }
  for (let i = 0; i < COLS; i++) {
    if (result[i] === 'correct') continue;
    const ch = guessChars[i];
    const idx = answerChars.findIndex((c, j) => !usedInAnswer[j] && c === ch);
    if (idx !== -1) {
      result[i] = 'present';
      usedInAnswer[idx] = true;
    }
  }
  return result;
}

export function PointsStarIcon({ pointsIconUrl }) {
  const [imgFailed, setImgFailed] = useState(false);
  if (pointsIconUrl && !imgFailed) {
    return (
      <img
        src={pointsIconUrl}
        alt=""
        className="wordle-points-img"
        width={22}
        height={22}
        onError={() => setImgFailed(true)}
      />
    );
  }
  return (
    <svg
      className="wordle-star-svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        fill="#c9a227"
        d="M12 2l2.9 7.4H22l-6 4.6 2.3 7L12 17.8 5.7 21l2.3-7-6-4.6h7.1L12 2z"
      />
    </svg>
  );
}

function WordleGame({
  answerWord,
  didYouKnow,
  didYouKnowImage,
  questionImage,
  questionText = '',
  badgeTitle = '',
  rules,
  badgeUrl,
  correctPoints,
  dayBonusPoints,
  pointsIconUrl,
  onComplete,
}) {
  const user = useSelector((state) => state.auth?.user);
  const demoPromoScheduledRef = useRef(false);
  const answer = useMemo(
    () => String(answerWord || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, COLS),
    [answerWord]
  );

  const questionImageUrl = s3PublicUrl(questionImage);
  const overlayQuestion = String(questionText ?? '').trim();
  const didYouKnowImageUrl = s3PublicUrl(didYouKnowImage);

  const [grid, setGrid] = useState(() =>
    Array.from({ length: ROWS }, () => Array(COLS).fill(''))
  );
  const [cellStates, setCellStates] = useState(() =>
    Array.from({ length: ROWS }, () => Array(COLS).fill(null))
  );
  const [rowIndex, setRowIndex] = useState(0);
  const [colIndex, setColIndex] = useState(0);
  const [status, setStatus] = useState('playing');
  const [keyboardHints, setKeyboardHints] = useState({});
  const [message, setMessage] = useState('Start guessing!');
  const [showDidYouKnow, setShowDidYouKnow] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [badgeImageFailed, setBadgeImageFailed] = useState(false);
  const [pendingCompletePayload, setPendingCompletePayload] = useState(null);
  const completeSentRef = useRef(false);
  const [wasCorrect, setWasCorrect] = useState(false);
  const [pointsToast, setPointsToast] = useState(null);

  useEffect(() => {
    setBadgeImageFailed(false);
  }, [badgeUrl]);

  const pointsAward = useMemo(() => {
    const n = Number(correctPoints);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [correctPoints]);

  const dayBonusAward = useMemo(() => {
    const n = Number(dayBonusPoints);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }, [dayBonusPoints]);

  const submitRow = useCallback(() => {
    if (status !== 'playing') return;
    if (isUserDemoBypass(user)) {
      if (!demoPromoScheduledRef.current) {
        demoPromoScheduledRef.current = true;
        demoGatePopupAndRedirect();
      }
      return;
    }
    if (colIndex !== COLS) {
      setMessage('You must enter 5 letters');
      return;
    }

    const guess = grid[rowIndex].join('');
    const evaluation = evaluateWordleGuess(guess, answer);
    if (!evaluation) return;

    setCellStates((prev) => {
      const next = prev.map((r) => [...r]);
      next[rowIndex] = evaluation;
      return next;
    });

    setKeyboardHints((prev) => {
      let next = { ...prev };
      for (let i = 0; i < COLS; i++) {
        const letter = guess[i].toUpperCase();
        next = mergeKeyHint(next, letter, evaluation[i]);
      }
      return next;
    });

    const won = guess.toUpperCase() === answer;

    const submittedAnswer = guess.toUpperCase();

    if (won) {
      const earned = pointsAward + dayBonusAward;
      setWasCorrect(true);
      setStatus('won');
      setMessage('Well done, you won!');
      setEarnedPoints(earned);
      if (dayBonusAward > 0) {
        setPointsToast(`+${dayBonusAward}`);
        window.setTimeout(() => setPointsToast(null), 2600);
      }
      setPendingCompletePayload({ earnedPoints: earned, answer: submittedAnswer, allCorrect: true });
      setShowDidYouKnow(true);
      return;
    }

    if (rowIndex >= ROWS - 1) {
      setWasCorrect(false);
      setStatus('lost');
      setMessage(`The word was ${answer}.`);
      setEarnedPoints(0);
      setPendingCompletePayload({ earnedPoints: 0, answer: submittedAnswer, allCorrect: false });
      setShowDidYouKnow(true);
      return;
    }

    setRowIndex((r) => r + 1);
    setColIndex(0);
    setMessage('');
  }, [status, colIndex, grid, rowIndex, answer, pointsAward, dayBonusAward, user]);

  const addLetter = useCallback(
    (letter) => {
      if (status !== 'playing') return;
      const ch = letter.toUpperCase();
      if (ch < 'A' || ch > 'Z') return;
      if (colIndex >= COLS) return;
      setGrid((g) => {
        const next = g.map((row) => [...row]);
        next[rowIndex][colIndex] = ch;
        return next;
      });
      setColIndex((c) => c + 1);
      if (message && message !== '') setMessage('');
    },
    [status, colIndex, rowIndex, message]
  );

  const deleteLetter = useCallback(() => {
    if (status !== 'playing') return;
    if (colIndex <= 0) return;
    setGrid((g) => {
      const next = g.map((row) => [...row]);
      next[rowIndex][colIndex - 1] = '';
      return next;
    });
    setColIndex((c) => c - 1);
  }, [status, colIndex, rowIndex]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (status !== 'playing') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key;
      if (k === 'Enter') {
        e.preventDefault();
        submitRow();
        return;
      }
      if (k === 'Backspace') {
        e.preventDefault();
        deleteLetter();
        return;
      }
      if (k.length === 1 && /[a-zA-Z]/.test(k)) {
        e.preventDefault();
        addLetter(k);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [status, submitRow, deleteLetter, addLetter]);

  if (answer.length !== COLS) {
    return (
      <p className="wordle-notification" style={{ textAlign: 'center' }}>
        Word must be exactly 5 letters in theme (stage_1).
      </p>
    );
  }

  const keyClass = (letter) => {
    const h = keyboardHints[letter];
    if (h === 'correct') return 'wordle-key kb-correct';
    if (h === 'present') return 'wordle-key kb-present';
    if (h === 'absent') return 'wordle-key kb-absent';
    return 'wordle-key';
  };

  const topRow = 'QWERTYUIOP'.split('');
  const midRow = 'ASDFGHJKL'.split('');
  const botRow = 'ZXCVBNM'.split('');

  const rulesList = Array.isArray(rules) ? rules : [];

  return (
    <div className="wordle-shell wordle-shell--viewport-lock">
      <header className="wordle-page-header">
        <div className="wordle-toolbar-strip" aria-hidden={false}>
          <div className="wordle-toolbar-end">
            {rulesList.length > 0 ? (
              <button
                type="button"
                className="wordle-icon-btn wordle-icon-rules"
                onClick={() => setShowRules(true)}
                aria-label="How to play"
                title="How to play"
              >
                ?
              </button>
            ) : null}
            <div className="wordle-points-group" title="Points">
              <PointsStarIcon pointsIconUrl={pointsIconUrl} />
              <span className="wordle-points-value">{earnedPoints}</span>
              {pointsToast ? <span className="wordle-points-toast">{pointsToast}</span> : null}
            </div>
          </div>
        </div>
        <div className="wordle-badge-header">
          <div className="wordle-badge-header__logo">
            {badgeUrl && !badgeImageFailed ? (
              <img
                src={badgeUrl}
                alt=""
                className="wordle-badge-img"
                onError={() => setBadgeImageFailed(true)}
              />
            ) : (
              <div className="wordle-badge-fallback" aria-hidden>
                Day 1
              </div>
            )}
          </div>
          <div className="wordle-badge-header__title">
            <span className="wordle-badge-title">
              {String(badgeTitle || '').trim() || 'Day 1'}
            </span>
          </div>
        </div>
      </header>

      <div className="wordle-root">
       

        <div className="wordle-split">
          <div className="wordle-split__left">
            <div id="wordle-notification" className="wordle-notification">
              {message}
            </div>
            <div className="wordle-grid" role="grid" aria-label="Wordle board">
              {grid.map((row, ri) => (
                <div key={ri} className="wordle-row" role="row">
                  {row.map((cell, ci) => {
                    const state = cellStates[ri][ci];
                    const cls = ['wordle-cell'];
                    if (cell) cls.push('filled');
                    if (state) cls.push(state);
                    return (
                      <div key={ci} className={cls.join(' ')} role="gridcell">
                        {cell}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="wordle-split__divider" aria-hidden />

          <div className="wordle-split__right">
            <div className="wordle-keyboard" aria-label="On-screen keyboard">
              <div className="wordle-key-row">
                {topRow.map((L) => (
                  <button
                    key={L}
                    type="button"
                    className={keyClass(L)}
                    onClick={() => addLetter(L)}
                    disabled={status !== 'playing'}
                  >
                    {L}
                  </button>
                ))}
              </div>
              <div className="wordle-key-row">
                {midRow.map((L) => (
                  <button
                    key={L}
                    type="button"
                    className={keyClass(L)}
                    onClick={() => addLetter(L)}
                    disabled={status !== 'playing'}
                  >
                    {L}
                  </button>
                ))}
              </div>
              <div className="wordle-key-row">
                <button
                  type="button"
                  className="wordle-key wide"
                  onClick={deleteLetter}
                  disabled={status !== 'playing'}
                  aria-label="Delete"
                >
                  ←
                </button>
                {botRow.map((L) => (
                  <button
                    key={L}
                    type="button"
                    className={keyClass(L)}
                    onClick={() => addLetter(L)}
                    disabled={status !== 'playing'}
                  >
                    {L}
                  </button>
                ))}
                <button
                  type="button"
                  className="wordle-key wide"
                  onClick={submitRow}
                  disabled={status !== 'playing'}
                >
                  Enter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showRules && rulesList.length > 0 && (
        <div
          className="wordle-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="wordle-rules-title"
        >
          <div className="wordle-modal">
            <h2 id="wordle-rules-title">How to play</h2>
            <ul className="wordle-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="wordle-modal-actions">
              <button type="button" onClick={() => setShowRules(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showDidYouKnow && (
        <div
          className="wordle-modal-overlay wordle-modal-overlay--dyk"
          role="dialog"
          aria-modal="true"
          aria-label="Answer details"
        >
          <div className="wordle-modal wordle-modal--dyk">
            {didYouKnowImageUrl ? (
              <img src={didYouKnowImageUrl} alt="" className="dyk-banner-image" />
            ) : (
              <div className="dyk-banner-fallback">Did you know?</div>
            )}

            <div className="dyk-body-box">
              {!wasCorrect ? (
                <p className="mcq-reveal dyk-reveal">
                 Correct Answer: <strong>{answer}</strong>
                </p>
              ) : null}
              {didYouKnow ? <p className="dyk-text">{didYouKnow}</p> : null}
            </div>

            <div className="wordle-modal-actions wordle-modal-actions--center">
              <button
                type="button"
                onClick={() => {
                  setShowDidYouKnow(false);
                  if (completeSentRef.current) return;
                  if (pendingCompletePayload && typeof onComplete === 'function') {
                    completeSentRef.current = true;
                    onComplete(pendingCompletePayload);
                  }
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WordleGame;
